import React from 'react';
import { ArrowLeftIcon, CreditCardIcon, CalendarIcon, DollarSignIcon, BuildingIcon, FileTextIcon, UserIcon, ClockIcon, ServerIcon, MessageSquareIcon, CheckIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import { Payment } from './PaymentsView';
import { formatCurrency } from '../../utils/currencyFormatter';
interface PaymentDetailProps {
  payment: Payment;
  onBack: () => void;
  locale: string;
}
export function PaymentDetail({
  payment,
  onBack,
  locale
}: PaymentDetailProps) {
  // Format dates for display
  const formattedPaymentDate = new Date(payment.paymentDate).toLocaleDateString(locale);
  const formattedCreatedAt = new Date(payment.createdAt).toLocaleString(locale);
  // Calculate total invoice amount
  const totalInvoiceAmount = payment.linkedInvoices.reduce((sum, invoice) => sum + invoice.invoiceTotal, 0);
  // Calculate total amount paid
  const totalAmountPaid = payment.linkedInvoices.reduce((sum, invoice) => sum + invoice.amountPaid, 0);
  // Check if this is a multi-vendor payment
  const uniqueVendors = new Set(payment.linkedInvoices.map(inv => inv.vendorId));
  const isMultiVendor = uniqueVendors.size > 1;
  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Cleared':
        return <CheckIcon className="h-4 w-4 text-green-500" data-id="element-9240" />;
      case 'Pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" data-id="element-9241" />;
      case 'Failed':
        return <XIcon className="h-4 w-4 text-red-500" data-id="element-9242" />;
      case 'Cancelled':
        return <AlertTriangleIcon className="h-4 w-4 text-gray-500" data-id="element-9243" />;
      default:
        return null;
    }
  };
  return <div className="bg-white shadow rounded-lg" data-id="element-9244">
      {/* Header */}
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-9245">
        <div className="flex items-center" data-id="element-9246">
          <button onClick={onBack} className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9247">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-9248" />
          </button>
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9249">
            Payment Details
          </h3>
        </div>
        <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${payment.status === 'Cleared' ? 'bg-green-100 text-green-800' : payment.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : payment.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9250">
          {payment.status}
        </span>
      </div>

      <div className="px-4 py-5 sm:px-6" data-id="element-9251">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9252">
          {/* Payment Header Info Section */}
          <div className="sm:col-span-3" data-id="element-9253">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-9254">
              <CreditCardIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-9255" />
              Payment Information
            </h4>
          </div>

          <div className="sm:col-span-1" data-id="element-9256">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9257">
              Payment Reference
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-9258">
              {payment.paymentReference}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-9259">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9260">Payment Date</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-9261">
              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-9262" />
              {formattedPaymentDate}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-9263">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9264">
              Payment Method
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-9265">{payment.method}</dd>
          </div>

          <div className="sm:col-span-1" data-id="element-9266">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9267">
              Payment Amount
            </dt>
            <dd className="mt-1 text-sm text-gray-900 font-semibold flex items-center" data-id="element-9268">
              <DollarSignIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-9269" />
              {formatCurrency(payment.amount, payment.currency, locale)}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-9270">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9271">Currency</dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-9272">{payment.currency}</dd>
          </div>

          {payment.externalSourceId && <div className="sm:col-span-1" data-id="element-9273">
              <dt className="text-sm font-medium text-gray-500" data-id="element-9274">
                External Source ID
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-9275">
                {payment.externalSourceId}
              </dd>
            </div>}

          {/* Linked Invoices Section */}
          <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-9276">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-9277">
              <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-9278" />
              Linked Invoices ({payment.linkedInvoices.length})
            </h4>
          </div>

          <div className="sm:col-span-3" data-id="element-9279">
            <div className="overflow-x-auto" data-id="element-9280">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-9281">
                <thead className="bg-gray-50" data-id="element-9282">
                  <tr data-id="element-9283">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9284">
                      Invoice Number
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9285">
                      Vendor
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9286">
                      Invoice Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9287">
                      Invoice Total
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9288">
                      Amount Paid
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9289">
                      Remaining Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-9290">
                  {payment.linkedInvoices.map(invoice => <tr key={invoice.invoiceId} className="hover:bg-gray-50" data-id="element-9291">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-9292">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9293">
                        {invoice.vendorName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9294">
                        {new Date(invoice.invoiceDate).toLocaleDateString(locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9295">
                        {formatCurrency(invoice.invoiceTotal, payment.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9296">
                        {formatCurrency(invoice.amountPaid, payment.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" data-id="element-9297">
                        <span className={invoice.remainingBalance === 0 ? 'text-green-600' : 'text-yellow-600'} data-id="element-9298">
                          {formatCurrency(invoice.remainingBalance || 0, payment.currency, locale)}
                          {invoice.remainingBalance === 0 && <CheckIcon className="inline-block h-4 w-4 ml-1 text-green-500" data-id="element-9299" />}
                        </span>
                      </td>
                    </tr>)}
                  {/* Summary row */}
                  <tr className="bg-gray-50 font-medium" data-id="element-9300">
                    <td colSpan={3} className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-9301">
                      Totals:
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9302">
                      {formatCurrency(totalInvoiceAmount, payment.currency, locale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9303">
                      {formatCurrency(totalAmountPaid, payment.currency, locale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-9304">
                      {formatCurrency(totalInvoiceAmount - totalAmountPaid, payment.currency, locale)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {isMultiVendor && <div className="mt-2 bg-yellow-50 p-2 rounded-md text-sm text-yellow-700 flex items-center" data-id="element-9305">
                <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-9306" />
                This payment is allocated across multiple vendors
              </div>}

            {payment.status === 'Cleared' && totalAmountPaid < totalInvoiceAmount && <div className="mt-2 bg-blue-50 p-2 rounded-md text-sm text-blue-700 flex items-center" data-id="element-9307">
                  <InfoIcon className="h-4 w-4 mr-2" data-id="element-9308" />
                  This payment partially covers the linked invoices. Additional
                  payments will be needed to clear the remaining balance.
                </div>}
          </div>

          {/* Metadata Section */}
          <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-9309">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-9310">
              <BuildingIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-9311" />
              Metadata
            </h4>
          </div>

          <div className="sm:col-span-1" data-id="element-9312">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9313">
              Organisation Unit
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-9314">
              {payment.orgUnitName}
            </dd>
          </div>

          <div className="sm:col-span-2" data-id="element-9315">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9316">
              Organisation Path
            </dt>
            <dd className="mt-1 text-sm text-gray-500" data-id="element-9317">
              {payment.orgUnitPath}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-9318">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9319">Created At</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-9320">
              <ClockIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-9321" />
              {formattedCreatedAt}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-9322">
            <dt className="text-sm font-medium text-gray-500" data-id="element-9323">Source System</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-9324">
              <ServerIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-9325" />
              {payment.sourceSystem}
            </dd>
          </div>

          {/* Notes Section (if available) */}
          {payment.notes && <>
              <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-9326">
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-9327">
                  <MessageSquareIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-9328" />
                  Notes
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-9329">
                <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md" data-id="element-9330">
                  {payment.notes}
                </dd>
              </div>
            </>}
        </dl>
      </div>
    </div>;
}
// For the InfoIcon in the component
function InfoIcon(props) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-9331">
      <circle cx="12" cy="12" r="10" data-id="element-9332" />
      <line x1="12" y1="16" x2="12" y2="12" data-id="element-9333" />
      <line x1="12" y1="8" x2="12.01" y2="8" data-id="element-9334" />
    </svg>;
}